<template>
  <div class="container">

    <div class="guidance-page-header">
      <div>
        <h4 class="mb-1 text-primary"><i class="bi bi-lightbulb mr-2 text-primary"></i>Smart Tips</h4>
        <p class="text-muted mb-0" style="font-size:13px;">Contextual suggestions based on how you use Dukakit</p>
      </div>
      <button v-if="!loading" class="btn btn-sm btn-outline-secondary" @click="fetch(true)">
        <i class="bi bi-arrow-clockwise mr-1"></i> Refresh
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading">
      <div v-for="n in 4" :key="n" class="guidance-card mb-3">
        <div class="guidance-card-inner">
          <div class="guidance-card-main">
            <div class="gsh gsh--title"></div>
            <div class="gsh gsh--body mt-2"></div>
            <div class="gsh gsh--body mt-1 short"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!cards.length" class="guidance-empty">
      <i class="bi bi-check-circle-fill" style="font-size:2.5rem;color:#28a745;"></i>
      <h5 class="mt-3 mb-1">You're all caught up</h5>
      <p class="text-muted mb-0">No suggestions right now. Check back as your business grows.</p>
    </div>

    <!-- Cards -->
    <div v-else>
      <div v-for="card in cards" :key="card.id" class="guidance-card mb-3">
        <div class="guidance-card-inner">
          <div class="guidance-card-main">
            <p class="guidance-headline">{{ card.headline }}</p>
            <p class="guidance-body">{{ card.body }}</p>

            <!-- Phase 3: AI Explanation -->
            <div v-if="card._explanation" class="guidance-ai-explain">
              <i class="bi bi-stars guidance-explain-icon"></i>
              <p class="guidance-explain-text">{{ card._explanation }}</p>
            </div>
            <div v-else-if="card._explaining" class="guidance-ai-explain">
              <div class="gsh gsh--body" style="width:90%"></div>
              <div class="gsh gsh--body mt-1" style="width:65%"></div>
            </div>

            <div class="guidance-ctas">
              <a
                v-for="(cta, i) in card.ctas"
                :key="i"
                :href="cta.route"
                :class="i === 0 ? 'btn btn-sm btn-primary mr-2' : 'btn btn-sm btn-outline-secondary mr-2'"
              >{{ cta.label }}</a>
              <button
                v-if="!card._explanation && !card._explaining"
                class="btn btn-sm btn-outline-primary"
                @click="explain(card)"
              ><i class="bi bi-stars mr-1"></i>Why this matters</button>
            </div>
          </div>
          <button class="guidance-dismiss" @click="dismiss(card)" title="Dismiss">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'GuidanceAssistant',

  data() {
    return {
      cards:   [],
      loading: false,
    };
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/guidance', {
          params: { channel: 'growth_center', limit: 20 },
        });
        this.cards = (data || []).map(c => ({ ...c, _explanation: null, _explaining: false }));
      } catch {
        // silently skip
      } finally {
        this.loading = false;
      }
    },

    async explain(card) {
      this.$set(card, '_explaining', true);
      try {
        const { data } = await axios.post('/api/ai/explain', {
          headline: card.headline,
          body:     card.body,
          type:     'tip',
        });
        this.$set(card, '_explanation', data.explanation || data.error || null);
      } catch {
        this.$set(card, '_explanation', 'Could not generate explanation.');
      } finally {
        this.$set(card, '_explaining', false);
      }
    },

    async dismiss(card) {
      this.cards = this.cards.filter(c => c.id !== card.id);
      try {
        await axios.post(`/api/guidance/${card.id}/dismiss`);
      } catch {
        // best-effort
      }
    },
  },
};
</script>

<style scoped>
.guidance-page {
  max-width: 680px;
  margin: 0 auto;
  padding: 20px 16px 80px;
}
.guidance-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}
.guidance-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}
.guidance-card-inner {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
}
.guidance-card-main { flex: 1; }
.guidance-headline {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 6px;
  line-height: 1.4;
}
.guidance-body {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.55;
  margin-bottom: 12px;
}
.guidance-ctas { display: flex; flex-wrap: wrap; gap: 6px; }
.guidance-dismiss {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  flex-shrink: 0;
  line-height: 1;
}
.guidance-dismiss:hover { color: #374151; }

/* ── AI explain block ── */
.guidance-ai-explain {
  display: flex;
  gap: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
}
.guidance-explain-icon { color: #f59e0b; font-size: 14px; flex-shrink: 0; margin-top: 1px; }
.guidance-explain-text { font-size: .72rem; color: #374151; line-height: 1.55; margin: 0; }

/* Empty state */
.guidance-empty { text-align: center; padding: 60px 20px; }

/* Shimmer */
.gsh {
  background: linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%);
  background-size: 200% 100%;
  animation: gsh 1.4s infinite;
  border-radius: 4px;
}
.gsh--title { height: 14px; width: 70%; }
.gsh--body  { height: 11px; width: 100%; }
.gsh--body.short { width: 55%; }
@keyframes gsh { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
</style>
